import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { API_BASE } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Mail, RefreshCw, Reply, Send, CheckCircle2, XCircle, Trophy, Inbox as InboxIcon, Link2Off, ArrowRight, PenLine, Copy, ExternalLink, FolderSymlink } from "lucide-react";
import { OutcomeReview } from "@/components/OutcomeReview";
import { useSyncedRefresh } from "@/hooks/useLiveSync";
import { useSignature, previewHtml, TONES } from "@/lib/emailCompose";
import SignatureSettings from "@/components/SignatureSettings";
import { DraftsPanel } from "@/components/claude/EmailDrafts";
import { useAuth } from "@/context/AuthContext";

function GoogleOAuthSetup({ onSaved }) {
  const [cfg, setCfg] = useState(null);
  const [cid, setCid] = useState("");
  const [sec, setSec] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  useEffect(() => {
    api.get("/email/oauth-config").then((r) => { setCfg(r.data); setCid(r.data.client_id || ""); })
      .catch((e) => setErr(e.response?.status === 403 ? "Only an owner can configure Google OAuth credentials." : "Could not load OAuth config."));
  }, []);
  const save = async () => {
    setSaving(true);
    try {
      const r = await api.post("/email/oauth-config", { client_id: cid.trim(), client_secret: sec.trim() });
      setCfg(r.data); setSec(""); toast.success("Google OAuth credentials saved"); onSaved?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    finally { setSaving(false); }
  };
  if (err) return <Card className="p-6 text-sm text-slate-500" data-testid="oauth-setup">{err}</Card>;
  if (!cfg) return <Card className="p-6 text-sm text-slate-400" data-testid="oauth-setup">Loading…</Card>;
  return (
    <Card className="p-6 text-left" data-testid="oauth-setup">
      <div className="text-base font-semibold text-black flex items-center gap-2"><Mail className="w-4 h-4 text-[#003E87]" /> Google OAuth setup</div>
      <p className="text-xs text-slate-500 mt-1">Paste the Client ID &amp; secret from your Google Cloud OAuth client. The secret is stored securely on the server and never shown again.{cfg.using_env ? " (A server default is active — save here to override it.)" : ""}</p>
      <label className="block mt-4"><span className="text-xs text-slate-500">Client ID</span>
        <input value={cid} onChange={(e) => setCid(e.target.value)} placeholder="…apps.googleusercontent.com" data-testid="oauth-client-id" className="w-full mt-1 text-sm border border-slate-200 rounded px-2 py-1.5 font-mono focus:border-[#003E87] outline-none" /></label>
      <label className="block mt-3"><span className="text-xs text-slate-500">Client secret {cfg.has_secret && <span className="text-emerald-600">· saved (leave blank to keep)</span>}</span>
        <input type="password" value={sec} onChange={(e) => setSec(e.target.value)} placeholder={cfg.has_secret ? "•••••••••• (unchanged)" : "GOCSPX-…"} data-testid="oauth-client-secret" className="w-full mt-1 text-sm border border-slate-200 rounded px-2 py-1.5 font-mono focus:border-[#003E87] outline-none" /></label>
      <div className="mt-3 rounded bg-slate-50 border border-slate-100 px-3 py-2">
        <div className="text-[11px] text-slate-500">Add this exact <b>Authorized redirect URI</b> to your Google Cloud OAuth client:</div>
        <div className="flex items-center gap-2 mt-1">
          <code className="text-[11px] text-slate-800 break-all flex-1">{cfg.redirect_uri}</code>
          <button onClick={() => { navigator.clipboard.writeText(cfg.redirect_uri); toast.success("Copied"); }} className="text-[#003E87] shrink-0" data-testid="oauth-copy-redirect"><Copy className="w-3.5 h-3.5" /></button>
        </div>
      </div>
      <Button onClick={save} disabled={saving || !cid.trim()} className="mt-4 bg-[#003E87] hover:bg-[#00306a] text-white" data-testid="oauth-save">{saving ? "Saving…" : "Save credentials"}</Button>
      {cfg.updated_by && <div className="text-[11px] text-slate-400 mt-2">Last updated by {cfg.updated_by}{cfg.updated_at ? ` · ${new Date(cfg.updated_at).toLocaleDateString()}` : ""}</div>}
    </Card>
  );
}

function DraftDialog({ ev, open, onOpenChange, canSend, onReconnect, onSent }) {
  return <DraftDialogImpl ev={ev} open={open} onOpenChange={onOpenChange} canSend={canSend} onReconnect={onReconnect} onSent={onSent} />;
}
export { DraftDialog };

function DraftDialogImpl({ ev, open, onOpenChange, canSend, onReconnect, onSent }) {
  const [text, setText] = useState("");
  const [meta, setMeta] = useState({});
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [tone, setTone] = useState("warm");
  const [showPreview, setShowPreview] = useState(false);
  const sig = useSignature();

  const runDraft = (toneKey) => {
    setLoading(true); setText("");
    api.post(`/email/events/${ev.id}/draft`, null, { params: { tone: toneKey } })
      .then((r) => { setText(r.data.draft || ""); setMeta(r.data); })
      .catch(() => toast.error("Could not draft a reply")).finally(() => setLoading(false));
  };

  useEffect(() => {
    if (open && ev) {
      setShowPreview(false); setTone("warm");
      if (ev.draft_cached) { setText(ev.draft_cached); setMeta({ to: ev.counterparty, subject: ev.subject }); setLoading(false); return; }
      runDraft("warm");
    }
    // eslint-disable-next-line
  }, [open, ev]);

  const pickTone = (k) => { setTone(k); runDraft(k); };
  const gmailUrl = `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(meta.to || "")}&su=${encodeURIComponent("Re: " + (meta.subject || ""))}&body=${encodeURIComponent(text)}`;
  const send = async () => {
    setSending(true);
    try {
      await api.post(`/email/events/${ev.id}/send`, { text });
      toast.success("Reply sent");
      onOpenChange(false); onSent && onSent();
    } catch (e) {
      if (e.response?.status === 403) { toast.error("Reconnect Gmail to enable one-click send"); onReconnect && onReconnect(); }
      else toast.error(e.response?.data?.detail || "Send failed");
    } finally { setSending(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="draft-dialog" className="max-w-lg">
        <DialogHeader><DialogTitle>Suggested reply {meta.to ? `to ${meta.to}` : ""}</DialogTitle></DialogHeader>
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-[11px] text-slate-400 mr-0.5">Tone</span>
          {TONES.map((t) => (
            <button key={t.key} data-testid={`draft-tone-${t.key}`} disabled={loading} onClick={() => pickTone(t.key)}
              className={`text-[11px] font-medium rounded-full px-2.5 py-1 transition disabled:opacity-50 ${tone === t.key ? "bg-[#003E87] text-white" : "text-slate-500 hover:bg-slate-100"}`}>
              {t.label}
            </button>
          ))}
          <button data-testid="draft-toggle-preview" onClick={() => setShowPreview((v) => !v)}
            className="ml-auto text-[11px] font-medium text-[#003E87] hover:underline">
            {showPreview ? "Edit" : "Preview"}
          </button>
        </div>
        {loading ? (
          <div className="py-10 text-center text-sm text-slate-400">Drafting…</div>
        ) : showPreview ? (
          <div data-testid="draft-preview" className="border border-slate-200 rounded-md p-3 min-h-[160px] max-h-[50vh] overflow-auto text-sm bg-white"
            dangerouslySetInnerHTML={{ __html: previewHtml(text, sig) }} />
        ) : (
          <Textarea rows={9} value={text} onChange={(e) => setText(e.target.value)} data-testid="draft-text" className="text-sm" />
        )}
        {showPreview && sig.enabled && (sig.html || "").trim() && (
          <div className="text-[11px] text-slate-400 -mt-1">Your saved signature is shown below the message.</div>
        )}
        {!canSend && (
          <div className="text-xs text-slate-500 -mt-1">
            Want to send without leaving your workspace?{" "}
            <button className="text-[#003E87] font-medium hover:underline" data-testid="draft-reconnect" onClick={onReconnect}>Reconnect Gmail</button> to enable one-click send.
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" data-testid="draft-copy" onClick={() => { navigator.clipboard.writeText(text); toast.success("Copied"); }}>
            <Copy className="w-4 h-4 mr-1" /> Copy
          </Button>
          {canSend ? (
            <Button className="bg-[#F4C400] hover:bg-[#e0b400] text-black" data-testid="draft-send"
              disabled={sending || loading || !text.trim()} onClick={send}>
              <Send className="w-4 h-4 mr-1" /> {sending ? "Sending…" : "Send now"}
            </Button>
          ) : (
            <Button className="bg-[#F4C400] hover:bg-[#e0b400] text-black" data-testid="draft-open-gmail"
              onClick={() => window.open(gmailUrl, "_blank")}>
              <ExternalLink className="w-4 h-4 mr-1" /> Send in Gmail
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const INTENT = {
  awaiting_reply: { label: "Awaiting your reply", cls: "bg-[#F4C400]/15 text-[#8a6d00]" },
  asked_for_pricing: { label: "Asked for pricing", cls: "bg-[#003E87]/10 text-[#003E87]" },
  asked_for_info: { label: "Asked for info", cls: "bg-[#003E87]/10 text-[#003E87]" },
  scheduling: { label: "Scheduling", cls: "bg-violet-100 text-violet-700" },
  decision_won: { label: "Sounds won", cls: "bg-emerald-100 text-emerald-700" },
  decision_lost: { label: "Sounds lost", cls: "bg-rose-100 text-rose-700" },
  went_quiet: { label: "Went quiet", cls: "bg-slate-100 text-slate-500" },
  general: { label: "Update", cls: "bg-slate-100 text-slate-600" },
};

export function IntentChip({ intent }) {
  const i = INTENT[intent] || INTENT.general;
  return <span className={`text-[10px] font-semibold rounded px-1.5 py-0.5 ${i.cls}`}>{i.label}</span>;
}

export function EmailRow({ ev, onApply, onIgnore, onDraft, showActions }) {
  return (
    <div data-testid={`email-row-${ev.id}`} className="flex items-start gap-3 py-3 border-b border-slate-100 last:border-0">
      <div className={`mt-1 shrink-0 w-7 h-7 rounded-full grid place-items-center ${ev.direction === "in" ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-500"}`}>
        {ev.direction === "in" ? <Reply className="w-3.5 h-3.5" /> : <Send className="w-3.5 h-3.5" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium text-black truncate">{ev.summary || ev.subject}</span>
          {ev.answered ? <span className="text-[10px] font-semibold rounded px-1.5 py-0.5 bg-emerald-100 text-emerald-700">Replied</span> : <IntentChip intent={ev.intent} />}
          {ev.decision && <span className="text-[10px] font-semibold rounded px-1.5 py-0.5 bg-amber-100 text-amber-800">suggest: {ev.decision}</span>}
        </div>
        <div className="text-xs text-slate-500 truncate mt-0.5">
          {ev.direction === "in" ? "From " : "To "}{ev.counterparty}
          {ev.client_name ? ` · ${ev.client_name}` : " · unmatched client"}
          {ev.bid_names?.length ? ` · ${ev.bid_names.join(", ")}` : ""}
          {ev.date ? ` · ${new Date(ev.date).toLocaleDateString()}` : ""}
        </div>
        <div className="text-xs text-slate-400 truncate mt-0.5 italic">“{ev.subject}”</div>

        {ev.direction === "in" && !ev.answered && (
          <button className="text-xs font-medium text-[#003E87] hover:underline mt-2 inline-flex items-center gap-1"
            data-testid={`email-draft-${ev.id}`} onClick={() => onDraft && onDraft(ev)}>
            <PenLine className="w-3.5 h-3.5" /> {ev.draft_cached ? "Review draft" : "Draft reply"}
          </button>
        )}

        {showActions && (
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            {ev.candidates?.length > 0 ? (
              ev.candidates.map((c) => (
                <Button key={c.client_id} size="sm" variant="outline" data-testid={`email-assign-${ev.id}-${c.client_id}`}
                  onClick={() => onApply(ev, { client_id: c.client_id })}>Log to {c.name}</Button>
              ))
            ) : (
              <Button size="sm" variant="outline" data-testid={`email-apply-${ev.id}`} onClick={() => onApply(ev, {})}>
                <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Log to bid{ev.bid_names?.length > 1 ? "s" : ""}
              </Button>
            )}
            {ev.decision === "won" && <Button size="sm" className="bg-emerald-600 hover:bg-emerald-500 text-white" data-testid={`email-won-${ev.id}`} onClick={() => onApply(ev, { outcome: "won" })}><Trophy className="w-3.5 h-3.5 mr-1" /> Record Won</Button>}
            {ev.decision === "lost" && <Button size="sm" className="bg-rose-600 hover:bg-rose-500 text-white" data-testid={`email-lost-${ev.id}`} onClick={() => onApply(ev, { outcome: "lost" })}><XCircle className="w-3.5 h-3.5 mr-1" /> Record Lost</Button>}
            <button className="text-xs text-slate-400 hover:text-slate-700" data-testid={`email-ignore-${ev.id}`} onClick={() => onIgnore(ev)}>Ignore</button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function Inbox({ embedded = false }) {
  const nav = useNavigate();
  const { user: me } = useAuth();
  const claudeAssistant = !!me?.features?.assistant_claude;
  const [params, setParams] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [tab, setTab] = useState(params.get(embedded ? "sub" : "tab") === "drafts" ? "drafts" : "review");
  const [draftCount, setDraftCount] = useState(0);
  const openDraft = params.get("draft");
  const [events, setEvents] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [relinking, setRelinking] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [draftEv, setDraftEv] = useState(null);

  const loadStatus = () => {
    api.get("/email/status").then((r) => setStatus(r.data)).catch(() => {});
    api.get("/email/drafts/count").then((r) => setDraftCount(r.data.count || 0)).catch(() => {});
  };
  const loadEvents = () => {
    if (tab === "drafts") return;
    const req = tab === "reply"
      ? api.get("/email/owed-queue")
      : api.get("/email/events", { params: { status: tab === "all" ? "all" : "review" } });
    req.then((r) => setEvents(r.data)).catch(() => {});
  };

  useEffect(() => { loadStatus(); }, []);
  useEffect(() => { if (status?.connected) loadEvents(); /* eslint-disable-next-line */ }, [tab, status?.connected]);
  useEffect(() => {
    if (!status?.connected) return;
    api.post("/email/sync").catch(() => {});           // kick an immediate sync on open (global 60s loop keeps it fresh after)
    // eslint-disable-next-line
  }, [status?.connected]);
  useSyncedRefresh(() => { loadEvents(); loadStatus(); });
  useEffect(() => {
    if (!status?.connected) return;
    // Refresh the tab you're actually viewing — must depend on `tab` so it
    // doesn't re-fetch a stale tab and wipe the list you're looking at.
    const refresh = setInterval(() => { loadEvents(); loadStatus(); }, 20000);
    return () => { clearInterval(refresh); };
    // eslint-disable-next-line
  }, [status?.connected, tab]);
  useEffect(() => {
    if (params.get("gmail") === "connected") { toast.success("Gmail connected"); setParams(embedded ? { tab: "inbox" } : {}); loadStatus(); }
    if (params.get("gmail") === "error") { toast.error("Could not connect Gmail — check the OAuth setup"); setParams(embedded ? { tab: "inbox" } : {}); }
    // eslint-disable-next-line
  }, [params]);

  const connect = () => { window.location.href = `${API_BASE}/oauth/gmail/login`; };
  const disconnect = async () => {
    if (!window.confirm("Disconnect this inbox? Auto-logging will stop.")) return;
    await api.delete("/email/disconnect"); toast.success("Disconnected"); loadStatus();
  };
  const sync = async () => {
    setSyncing(true);
    try {
      await api.post("/email/sync");
      toast.success("Syncing your inbox — new items appear in a moment");
      setTimeout(() => { loadEvents(); loadStatus(); setSyncing(false); }, 6000);
    } catch (e) { toast.error(e.response?.data?.detail || "Sync failed"); setSyncing(false); }
  };
  const relinkAll = async () => {
    setRelinking(true);
    try {
      await api.post("/email/relink-all");
      toast.success("Re-linking every project's email — runs in the background, can take a few minutes");
      setTimeout(() => { loadEvents(); loadStatus(); setRelinking(false); }, 8000);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not start"); setRelinking(false); }
  };
  const apply = async (ev, body) => {
    try {
      await api.post(`/email/events/${ev.id}/apply`, body);
      toast.success(body.outcome ? `Recorded ${body.outcome}` : "Logged to bid");
      loadEvents(); loadStatus();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not apply"); }
  };
  const ignore = async (ev) => { await api.post(`/email/events/${ev.id}/ignore`); loadEvents(); loadStatus(); };
  const autodraft = async () => {
    setDrafting(true);
    try {
      const r = await api.post("/email/autodraft");
      const n = r.data.drafted || 0;
      toast.success(n ? `Pre-drafted ${n} owed repl${n === 1 ? "y" : "ies"}` : "Nothing owed to draft right now");
      loadEvents();
    } catch { toast.error("Could not pre-draft"); }
    finally { setDrafting(false); }
  };
  const emailBrief = async () => {
    try {
      const r = await api.post("/email/digest-now");
      toast.success(r.data.sent ? "Your daily brief is on its way to your inbox" : "Nothing to summarize right now — you're all caught up");
    } catch { toast.error("Could not send your brief"); }
  };

  return (
    <div className={embedded ? "" : "px-6 sm:px-8 py-8"} data-testid="inbox-page">
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
        {!embedded && <div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-black flex items-center gap-2">
            <Mail className="w-5 h-5 text-[#003E87]" /> Email agent
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">Connect Google once — each project pulls in its own email threads automatically (by name, number &amp; client), keeps them updated, and flags only what genuinely needs a reply or a timely follow-up. Mail that doesn't match a project stays out of your way.</p>
        </div>}
        {status?.connected && (
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={emailBrief} data-testid="inbox-email-brief">
              <Mail className="w-4 h-4 mr-1" /> Email my brief
            </Button>
            {!claudeAssistant && (
              <Button variant="outline" onClick={autodraft} disabled={drafting} data-testid="inbox-autodraft">
                <PenLine className={`w-4 h-4 mr-1 ${drafting ? "animate-pulse" : ""}`} /> Draft all owed
              </Button>
            )}
            <Button variant="outline" onClick={relinkAll} disabled={relinking} data-testid="inbox-relink-all">
              <FolderSymlink className={`w-4 h-4 mr-1 ${relinking ? "animate-pulse" : ""}`} /> {relinking ? "Re-linking…" : "Re-link all projects"}
            </Button>
            <Button variant="outline" onClick={sync} disabled={syncing} data-testid="inbox-sync">
              <RefreshCw className={`w-4 h-4 mr-1 ${syncing ? "animate-spin" : ""}`} /> {syncing ? "Syncing…" : "Sync now"}
            </Button>
            <button onClick={disconnect} data-testid="inbox-disconnect" className="text-xs text-slate-400 hover:text-rose-600 flex items-center gap-1"><Link2Off className="w-3.5 h-3.5" /> {status.email}</button>
          </div>
        )}
      </div>

      <OutcomeReview onChange={() => { loadEvents(); loadStatus(); }} />

      {status?.connected && status.backfill_status === "running" && (
        <Card data-testid="inbox-backfill-banner" className="p-4 mb-4 border-[#003E87]/30 bg-[#003E87]/5 flex items-center gap-3">
          <RefreshCw className="w-4 h-4 text-[#003E87] shrink-0 animate-spin" />
          <div className="flex-1 text-sm text-[#003E87]">
            <span className="font-semibold">Deep-scanning your inbox…</span> reading your full history and pulling every email tied to a client — {status.backfill_scanned || 0} scanned, {status.backfill_logged || 0} logged to bids so far. You can keep working; this runs in the background.
          </div>
        </Card>
      )}

      {status?.connected && !status.can_send && (
        <Card data-testid="inbox-reconnect-banner" className="p-4 mb-4 border-[#F4C400]/50 bg-[#F4C400]/10 flex items-center gap-3">
          <Send className="w-4 h-4 text-[#8a6d00] shrink-0" />
          <div className="flex-1 text-sm text-[#5c4a00]">
            <span className="font-semibold">Turn on one-click send.</span> Reconnect Gmail once to grant send access, then reply straight from your workspace — no Gmail tab.
          </div>
          <Button size="sm" className="bg-[#F4C400] hover:bg-[#e0b400] text-black shrink-0" data-testid="inbox-reconnect" onClick={connect}>Reconnect Gmail</Button>
        </Card>
      )}

      {!status ? (
        <div className="text-sm text-slate-400 py-16 text-center">Loading…</div>
      ) : !status.configured ? (
        <div className="max-w-lg mx-auto"><GoogleOAuthSetup onSaved={loadStatus} /></div>
      ) : !status.connected ? (
        <>
        <div className="max-w-lg mx-auto"><DraftsPanel hideEmpty initialDraft={openDraft} onChange={loadStatus} /></div>
        <Card className="p-8 max-w-lg mx-auto" data-testid="inbox-connect-card">
          <div className="flex items-center gap-2.5 mb-1">
            <div className="w-9 h-9 rounded-full bg-[#003E87]/10 flex items-center justify-center shrink-0"><InboxIcon className="w-5 h-5 text-[#003E87]" /></div>
            <div className="text-base font-semibold text-black">Connect Google</div>
          </div>
          <p className="text-sm text-slate-500 mt-1">One secure sign-in with Google. You'll see Google's own permission screen and choose your account — we never see your password.</p>
          <ul className="mt-4 space-y-2 text-sm text-slate-600">
            <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" /> <span><b>Reads</b> your client email threads to auto-log contact and follow-ups.</span></li>
            <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" /> <span><b>Sends</b> only the replies you review and approve — nothing goes out on its own.</span></li>
            <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" /> <span><b>Files</b> project documents to a "GridForge Projects" folder in your Drive (optional, turn on later).</span></li>
          </ul>
          <Button className="mt-5 w-full bg-[#F4C400] hover:bg-[#e0b400] text-black font-semibold" onClick={connect} data-testid="inbox-connect">
            <Mail className="w-4 h-4 mr-1.5" /> Continue with Google
          </Button>
          <div className="mt-3 rounded-lg bg-slate-50 border border-slate-100 px-3 py-2 text-[11px] text-slate-500 leading-relaxed" data-testid="inbox-connect-note">
            First time you may see <b>"Google hasn't verified this app"</b> — that's expected for a private tool. Click <b>Advanced</b> → <b>Continue to GridForge</b> to proceed. You can disconnect anytime.
          </div>
        </Card>
        <div className="mt-4 max-w-lg mx-auto"><GoogleOAuthSetup onSaved={loadStatus} /></div>
        <div className="mt-4"><SignatureSettings /></div>
        </>
      ) : (
        <>
          <SignatureSettings />
          <div className="flex items-center gap-3 mb-4">
            <div className="inline-flex rounded-lg border border-slate-200 p-0.5 bg-slate-50">
              {[["review", `Needs review${status.review_count ? ` (${status.review_count})` : ""}`], ["reply", "Reply queue"], ["drafts", `Drafts${draftCount ? ` (${draftCount})` : ""}`], ["all", "All activity"]].map(([k, l]) => (
                <button key={k} data-testid={`inbox-tab-${k}`} onClick={() => setTab(k)}
                  className={`text-sm px-3 py-1.5 rounded-md transition ${tab === k ? "bg-white shadow-sm text-black font-medium" : "text-slate-500 hover:text-slate-800"}`}>{l}</button>
              ))}
            </div>
            {status.last_sync_at && <span className="text-xs text-slate-400">last synced {new Date(status.last_sync_at).toLocaleString()}</span>}
          </div>
          {tab === "drafts" ? <DraftsPanel initialDraft={openDraft} onChange={loadStatus} /> : (
          <Card className="p-5">
            {events.length === 0 ? (
              <div className="text-center py-10 text-sm text-slate-400">
                {tab === "review" ? "Nothing needs review — high-confidence emails were auto-logged." : tab === "reply" ? "You're all caught up — no client emails are waiting on a reply." : "No email activity yet. Hit “Sync now”."}
              </div>
            ) : (
              events.map((ev) => <EmailRow key={ev.id} ev={ev} onApply={apply} onIgnore={ignore} onDraft={setDraftEv} showActions={tab === "review"} />)
            )}
          </Card>
          )}
        </>
      )}
      <DraftDialog ev={draftEv} open={!!draftEv} onOpenChange={(o) => { if (!o) setDraftEv(null); }}
        canSend={!!status?.can_send} onReconnect={connect}
        onSent={() => { setDraftEv(null); loadEvents(); loadStatus(); }} />
    </div>
  );
}
